import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import type { BackfillProgress } from "../../hooks/backfillProgress";
import { queryKeys } from "../../utils/queryKeys";
import { refreshStep, type RefreshTrack } from "./gameSync";

/**
 * Refresh the games lists when the user's analysis job lands new games (the rule is refreshStep's): every games
 * query (`['games', …]`: the home's useGamesSince, the Library's card view, Openings, the full list), the analyzed
 * ids (the Library's unanalyzed rows, the landing's Reviewed tag) and the tag stats. Call it ONCE, from the
 * always-mounted banner: every other useBackfillJob consumer would refresh again.
 */
export function useRefreshGamesOnJobEnd(progress: BackfillProgress): void {
  const queryClient = useQueryClient();
  const track = useRef<RefreshTrack | null>(null);
  // Runs on every render (progress is derived fresh each time): refreshStep answers true once per job, so a re-run
  // with nothing new is a no-op.
  useEffect(() => {
    const step = refreshStep(track.current, progress);
    track.current = step.track;
    if (!step.refresh) return;
    void queryClient.invalidateQueries({ queryKey: queryKeys.games });
    void queryClient.invalidateQueries({ queryKey: queryKeys.analyzedIds });
  }, [progress, queryClient]);
}
